import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../../UserContext';
import './ProjectDetails.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMobile,
    faDna,
    faTooth,
    faVial,
    faFlask,
    faDroplet,
    faAtom,
    faCode,
    faBacteria,
    faPlus,
    faCirclePlus,
    faPhone,
    faMobileScreen,
    faMobilePhone,
    faChartGantt,

    faUserGear,
    faArrowLeft,
    faPlusCircle,
    faDigitalTachograph,
    faTimes,
    faCircleNodes,
    faFlaskVial,
    faOilCan,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import ProjectAccountAssignment from './ProjectAccountAssignment/ProjectAccountAssignment';
import PatientMetadataEditor from './PatientMetadataEditor/PatientMetadataEditor';
import ProjectInfoModal from './ProjectInfoModal';
import OmicsViewModal from '../../SampleManager/OmicsView/OmicsView';

const getReportIcon = (reportType) => {
    switch(reportType) {
        case 'Genomics': return faDna;
        case 'Gut Health': return faBacteria;
        case 'Oral Health': return faTooth;
        case 'Metabolome Plasma': return faDroplet;
        case 'Metabolome Urine': return faFlaskVial;
        case 'Metabolome Lipids': return faOilCan;
        case 'Proteomics': return faAtom;
        case 'Transcriptome': return faCircleNodes;
        default: return faDna;
    }
};


function ProjectDetails({ projectId, projectData, onBackClick }) {
    const { user } = useUser();
    const [allPatients, setAllPatients] = useState([]);
    const [patients, setPatients] = useState([]);
    const [subprojects, setSubprojects] = useState([]);
    const [selectedSubproject, setSelectedSubproject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [showMetadataModal, setShowMetadataModal] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
    const [showProjectInfoModal, setShowProjectInfoModal] = useState(false);
    const [displayedPatients, setDisplayedPatients] = useState([]);
    const [filteredPatients, setFilteredPatients] = useState([]);
    const [page, setPage] = useState(0);
    const PATIENTS_PER_PAGE = 40;
    const contentRef = useRef(null);
    const scrollTimeout = useRef(null);
    const [showAddPatientModal, setShowAddPatientModal] = useState(false);
    const [addingPatient, setAddingPatient] = useState(false);
    const [selectedSubprojectForAdd, setSelectedSubprojectForAdd] = useState(null);
    const [showOmicsModal, setShowOmicsModal] = useState(false);
    const [selectedOmicsType, setSelectedOmicsType] = useState(null);
    const [selectedOmicsId, setSelectedOmicsId] = useState(null);
    const [omicsModalData, setOmicsModalData] = useState({});
    const [numberOfPatientsToAdd, setNumberOfPatientsToAdd] = useState(1);

    const fetchSubprojectsData = async () => {
        try {
            const response = await fetch(`https://service9.szapfs.org/parent-project/${projectId}/subprojects`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setSubprojects([{ project_id: 'all', name: 'All' }, ...data]);
            if (!selectedSubproject) {
                setSelectedSubproject({ project_id: 'all', name: 'All' });
            }
        } catch (err) {
            console.error('Error fetching subprojects:', err);
            setError('Failed to load subprojects. Please try again later.');
        }
    };

    const fetchAllPatients = async () => {
        try {
            setLoading(true);
            let data = [];
            const promises = subprojects
                .filter(sp => sp.project_id !== 'all')
                .map(sp =>
                    fetch(`https://service9.szapfs.org/project/${projectId}/${sp.project_id}/patients`)
                        .then(res => res.json())
                        .then(patients => {
                            console.log(`Response from patients endpoint for ${sp.project_id}:`, patients);
                            return patients.map(p => ({
                                ...p,
                                project_id: sp.project_id,
                                // Map available_reports to reports format if it exists
                                reports: p.available_reports ? Object.entries(p.available_reports).map(([type, tables]) => ({
                                    type,
                                    tables
                                })) : []
                            }));
                        })
                );

            const results = await Promise.all(promises);
            data = Array.from(new Map(results.flat().map(patient => [patient.patient_id, patient])).values());

            // No need to fetch reports separately as they're now included in the patients response
            const patientsWithReports = data.sort((a, b) => {
                const aNum = parseInt(a.patient_id.split('_')[2]) || 0;
                const bNum = parseInt(b.patient_id.split('_')[2]) || 0;
                return aNum - bNum;
            });

            setAllPatients(patientsWithReports);
            if (selectedSubproject?.project_id === 'all') {
                setPatients(patientsWithReports);
            }
        } catch (err) {
            console.error('Error fetching all patients:', err);
            setError('Failed to load patients. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchPatients = async () => {
        if (!selectedSubproject) return;

        try {
            setLoading(true);
            if (selectedSubproject.project_id === 'all') {
                setPatients(allPatients);
                setLoading(false);
            } else {
                const response = await fetch(`https://service9.szapfs.org/project/${projectId}/${selectedSubproject.project_id}/patients`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();

                // Log the response to see the structure
                console.log('Response from patients endpoint for selected subproject:', data);

                // No need to fetch reports separately as they're now included in the patients response
                const patientsWithReports = data.map(patient => ({
                    ...patient,
                    project_id: selectedSubproject.project_id,
                    // Map available_reports to reports format if it exists
                    reports: patient.available_reports ? Object.entries(patient.available_reports).map(([type, tables]) => ({
                        type,
                        tables
                    })) : []
                })).sort((a, b) => {
                    const aNum = parseInt(a.patient_id.split('_')[2]) || 0;
                    const bNum = parseInt(b.patient_id.split('_')[2]) || 0;
                    return aNum - bNum;
                });

                setPatients(patientsWithReports);
            }
        } catch (err) {
            console.error('Error fetching patients:', err);
            setError('Failed to load patients. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddPatient = async (subproject, count) => {
        if (!subproject || count < 1) {
            alert('Please select a subproject and specify a valid number of patients.');
            return;
        }
    
        try {
            setAddingPatient(true);
            const response = await fetch(`https://service9.szapfs.org/project/${projectId}/${subproject.project_id}/add-patient`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json', // Important for sending JSON body
                },
                body: JSON.stringify({ count: count }) // Send count in the body
            });
    
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Failed to add patients' }));
                throw new Error(errorData.error || 'Failed to add patients');
            }
    
            const result = await response.json();
            console.log(result.message); // Log success message from backend
    
            // Refresh all patients to get updated list
            await fetchAllPatients(); // This will update allPatients with the new patient(s)
            await fetchPatients();    // This will update the current view with the latest data
            
            setShowAddPatientModal(false);
            setSelectedSubprojectForAdd(null);
            setNumberOfPatientsToAdd(1); // Reset count
        } catch (error) {
            console.error('Error adding patient(s):', error);
            alert(`Failed to add patient(s): ${error.message}. Please try again.`);
        } finally {
            setAddingPatient(false);
        }
    };

    const handleMetadataSave = async (updatedData) => {
        try {
            const response = await fetch('https://service9.szapfs.org/patient/metadata', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    patient_id: selectedPatient.patient_id,
                    ...updatedData
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update');
            }

            // Refresh both patient lists to show updated data
            await fetchAllPatients();
            await fetchPatients();

            // Then close modal
            setShowMetadataModal(false);
            setSelectedPatient(null);
        } catch (error) {
            console.error('Error updating metadata:', error);
            alert('Failed to update patient information. Please try again.');
        }
    };

    useEffect(() => {
        fetchSubprojectsData();
    }, [projectId]);

    useEffect(() => {
        if (subprojects.length > 0) {
            fetchAllPatients();
        }
    }, [projectId, subprojects]);

    useEffect(() => {
        fetchPatients();
    }, [selectedSubproject, allPatients]);

    useEffect(() => {
        setDisplayedPatients([]);
        setFilteredPatients([]);
        setSearchTerm('');
        setPage(0);
    }, [selectedSubproject]);

    useEffect(() => {
        const searchLower = searchTerm.toLowerCase();
        const filtered = searchTerm
            ? patients.filter(patient =>
                patient.patient_id.toLowerCase().includes(searchLower) ||
                (patient.name && patient.name.toLowerCase().includes(searchLower))
            )
            : patients;

        setFilteredPatients(filtered);
        setDisplayedPatients(filtered.slice(0, PATIENTS_PER_PAGE));
    }, [searchTerm, patients]);

    const handleScroll = useCallback((e) => {
        if (loading) return;

        if (scrollTimeout.current) {
            clearTimeout(scrollTimeout.current);
        }

        scrollTimeout.current = setTimeout(() => {
            const element = e.target;
            const threshold = element.clientHeight * 1.2;
            const remainingScroll = element.scrollHeight - (element.scrollTop + element.clientHeight);

            if (remainingScroll < threshold) {
                const currentLength = displayedPatients.length;
                const totalLength = filteredPatients.length;

                if (currentLength < totalLength) {
                    const nextBatch = filteredPatients.slice(
                        currentLength,
                        currentLength + PATIENTS_PER_PAGE
                    );
                    setDisplayedPatients(prev => [...prev, ...nextBatch]);
                }
            }
            scrollTimeout.current = null;
        }, 100);
    }, [loading, displayedPatients.length, filteredPatients, PATIENTS_PER_PAGE]);

    useEffect(() => {
        const content = contentRef.current?.querySelector('.PMP-content');
        if (content) {
            content.addEventListener('scroll', handleScroll);
            return () => {
                content.removeEventListener('scroll', handleScroll);
                if (scrollTimeout.current) {
                    clearTimeout(scrollTimeout.current);
                }
            };
        }
    }, [handleScroll]);

    const handleAddAccount = (event, patient) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setModalPosition({
            top: rect.top,
            left: rect.right + 10
        });
        setSelectedPatient(patient);
        setShowAccountModal(true);
    };

    const handleAssignAccount = async (patient, accountId) => {
        // After successful assignment, refresh all patients to get updated omics test and assignment details
        await fetchAllPatients(); // This will update allPatients with the new patient
        await fetchPatients(); // This will update the current view with the latest data
    };

    const getInitials = (name) => {
        if (!name) return '??';
        const words = name.split(' ');
        return words.slice(0, 2).reduce((acc, word) => acc + (word ? word.charAt(0) : ''), '');
    };

    const handleProjectInfo = () => {
        setShowProjectInfoModal(true);
    };

    const handleSubprojectClick = (subproject) => {
        setSelectedSubproject(subproject);
        setDisplayedPatients([]);
        setFilteredPatients([]);
        setSearchTerm('');
        setPage(0);
    };

    if (error) {
        return (
            <div className="PMP-container">
                <div className="PMP-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="PMP-container" ref={contentRef}>
            <div className="PMP-header">
                <button className="PMP-back" onClick={onBackClick}>
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>

                <div>
                    <input
                        className='PMP-search'
                        type="text"
                        placeholder="Search patients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="PMP-headerright"    onClick={() => setShowAddPatientModal(true)}
                >
                <FontAwesomeIcon icon={faPlusCircle} className='PMP-headerrighticon'  />




                </div>
                {!loading && (displayedPatients.length > 0 || subprojects.filter(sp => sp.project_id !== 'all').length === 0) && (
                    <div className="PMP-headerrighti" onClick={handleProjectInfo}>
                        <div className="PMP-bri" >
                            <FontAwesomeIcon icon={faDigitalTachograph} className='PMP-headerrighticon1' />
                            <div className="PMP-headerrightt" >
                                Project Information
                            </div>
                        </div>
                    </div>
                )}

            </div>

            <div className="PMP-subprojects">
                {subprojects.map(subproject => (
                    <div
                        key={subproject.project_id}
                        className={`PMP-subproject ${selectedSubproject?.project_id === subproject.project_id ? 'selected' : ''}`}
                        onClick={() => handleSubprojectClick(subproject)}
                    >
                        {subproject.name || subproject.project_id}
                    </div>
                ))}
            </div>

            <div className="PMP-content"
                onScroll={() => {
                    if (showAccountModal || showMetadataModal) {
                        const modal = document.querySelector('.PMPAS-modal');
                        if (modal) {
                            modal.classList.add('PMPFadeaway');
                            setTimeout(() => {
                                setShowAccountModal(false);
                                setShowMetadataModal(false);
                            }, 150);
                        }
                    }
                }}
            >
                <div className={`PMP-grid ${loading ? 'loading' : ''}`}>
                    {loading && displayedPatients.length === 0 ? (
                        <div className="PMP-loading">Loading...</div>
                    ) : (
                        displayedPatients.map((patient, index) => (
                            <div key={patient.patient_id} className="PMP-patient-tile">
                                <div className="PMP-patient-header">
                                    <div className="PMP-patient-initial">
                                        {user?.role === 'Reviewer' ? `P${index + 1}` : getInitials(patient?.name)}

                                        {user?.role !== 'Reviewer' && (
                                            <div className="PMP-edit-icon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedPatient(patient);
                                                    setShowMetadataModal(true);
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faUser}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="PMP-patient-info">
                                        <div className="PMP-patient-name">
                                            <span>{user?.role === 'Reviewer' ? `Person ${index + 1}` : (patient?.name || '')}</span>
                                            <FontAwesomeIcon
                                                icon={patient?.Account_ID ? faMobilePhone : faCirclePlus}
                                                style={{
                                                    color: patient?.Account_ID ? '#20558aec' : 'lightgrey',
                                                    cursor: user?.role === 'Reviewer' ? 'default' : 'pointer',
                                                    opacity: user?.role === 'Reviewer' ? 0.5 : 1
                                                }}
                                                onClick={(e) => user?.role !== 'Reviewer' ? handleAddAccount(e, patient) : null}
                                            />
                                        </div>
                                        <div className="PMP-patient-details">
                                            <span>{patient?.date_of_birth || ''}</span>
                                            <span> - {patient?.gender || ''} - </span>
                                            <span>{patient?.patient_id || ''}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="PMP-report-slots">
                                            {(patient?.reports || []).map((report, index) => (
                                                <div
                                                    key={index}
                                                    className="PMP-report-slot"
                                                    title={report?.type || ''}
                                                    onClick={() => {
                                                        // Handle click to open OmicsView with this report
                                                        if (report?.tables?.length > 0) {
                                                            // Map the report type to the expected OmicsView type
                                                            let omicsType;
                                                            switch(report.type) {
                                                                case 'WGS':
                                                                    omicsType = 'Whole Genome';
                                                                    break;
                                                                case 'Gut Health':
                                                                    omicsType = 'Gut Health';
                                                                    break;
                                                                case 'Oral Health':
                                                                    omicsType = 'Oral Health';
                                                                    break;
                                                                case 'Transcriptome':
                                                                    omicsType = 'Transcriptome';
                                                                    break;
                                                                default:
                                                                    omicsType = report.type;
                                                            }

                                                            setSelectedOmicsType(omicsType);
                                                            setSelectedOmicsId(report.tables[0]); // Use the first table ID
                                                            setOmicsModalData({
                                                                patientId: patient.patient_id,
                                                                ...patient
                                                            });
                                                            setShowOmicsModal(true);
                                                        }
                                                    }}
                                                    style={{ cursor: report?.tables?.length > 0 ? 'pointer' : 'default' }}
                                                >
                                                    <div className="PMP-report-tables-tooltip">
                                                        <div className="PMP-report-tables-content">
                                                            <div className="PMP-report-tables-title">{report?.type || ''}</div>
                                                            {report?.tables?.length > 0 ? (
                                                                report.tables.map((table, i) => (
                                                                    <div key={i} className="PMP-report-table-name">
                                                                        {table || ''}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="PMP-report-table-name"></div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <FontAwesomeIcon
                                                        icon={getReportIcon(report?.type)}
                                                        className="PMP-report-icon"
                                                    />
                                                    <span className="PMP-report-label">
                                                        {report?.type ? (
                                                            report.type.startsWith('Metabolome') ?
                                                                report.type.split(' ')[1] :
                                                                report.type.split(' ')[0]
                                                        ) : ''}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                            </div>
                        ))
                    )}
                </div>
                {loading && displayedPatients.length > 0 && (
                    <div className="PMP-loading-more">Loading more patients...</div>
                )}
                {!loading && displayedPatients.length < filteredPatients.length && (
                    <div className="PMP-scroll-info">Scroll down to load more patients</div>
                )}
            </div>
            {showAccountModal && (
                <ProjectAccountAssignment
                    position={modalPosition}
                    onClose={() => setShowAccountModal(false)}
                    patient={selectedPatient}
                    onAssign={handleAssignAccount}
                />
            )}
            {showMetadataModal && selectedPatient && (
                <PatientMetadataEditor
                    patient={selectedPatient}
                    onClose={() => {
                        setShowMetadataModal(false);
                        setSelectedPatient(null);
                    }}
                    onSave={handleMetadataSave}
                />
            )}
            {showProjectInfoModal && (
                <ProjectInfoModal
                    isOpen={showProjectInfoModal}
                    onClose={() => setShowProjectInfoModal(false)}
                    project={projectData || { id: projectId }}
                    patients={allPatients}
                    subprojects={subprojects}
                    reports={allPatients.reduce((acc, patient) => {
                        acc[patient.patient_id] = {
                            reports: patient.reports || []
                        };
                        return acc;
                    }, {})}
                    onSubprojectsUpdate={async () => {
                        await fetchSubprojectsData();
                        await fetchAllPatients();
                    }}
                />
            )}
         {showAddPatientModal && (
            <div className="PMP-modal">
                <div className="PMP-modal-content PMP-add-patient-modal">
                    <div className="PMP-modal-header">
                        <h3>Add Patients to Subproject</h3> {/* Updated title */}
                        <button
                            className="PMP-modal-close"
                            onClick={() => {
                                setShowAddPatientModal(false);
                                setSelectedSubprojectForAdd(null);
                                setNumberOfPatientsToAdd(1); // Reset on close
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="PMP-subprojects-list">
                        {subprojects
                            .filter(sp => sp.project_id !== 'all')
                            .map(subproject => (
                                <div
                                    key={subproject.project_id}
                                    className={`PMP-subproject-option ${
                                        selectedSubprojectForAdd?.project_id === subproject.project_id ? 'selected' : ''
                                    }`}
                                    onClick={() => setSelectedSubprojectForAdd(subproject)}
                                >
                                    <div className="PMP-subproject-option-content">
                                        <div>
                                            {subproject.project_id}
                                        </div>
                                        <div className="PMP-subproject-option-description">
                                            {subproject.description}
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>

                    {/* New Section for Number of Patients */}
                    {selectedSubprojectForAdd && (
                        <div className="PMP-add-patient-count-section" style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label htmlFor="numPatients" style={{ fontWeight: 'bold' }}>Number of patients to add:</label>
                            <input
                                type="number"
                                id="numPatients"
                                value={numberOfPatientsToAdd}
                                onChange={(e) => {
                                    const val = e.target.value === "" ? "" : parseInt(e.target.value, 10);
                                    if (val === "" || val >= 1) {
                                        setNumberOfPatientsToAdd(val);
                                    }
                                }}
                                min="1"
                                className="PMP-input-number-patients" // Add styling for this class if needed
                                style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px', width: '80px' }}
                            />
                        </div>
                    )}

                    <div className="PMP-modal-footer">
                        <button
                            className="PMP-button"
                            disabled={!selectedSubprojectForAdd || addingPatient || !numberOfPatientsToAdd || Number(numberOfPatientsToAdd) < 1}
                            onClick={() => handleAddPatient(selectedSubprojectForAdd, Number(numberOfPatientsToAdd))}
                        >
                            {addingPatient
                                ? `Adding ${numberOfPatientsToAdd} ${Number(numberOfPatientsToAdd) > 1 ? 'Patients' : 'Patient'}...`
                                : `Add ${numberOfPatientsToAdd || 0} ${Number(numberOfPatientsToAdd) > 1 || Number(numberOfPatientsToAdd) === 0 ? 'Patients' : 'Patient'}`}
                        </button>
                    </div>
                </div>
            </div>
        )}

            {/* OmicsView Modal */}
            {showOmicsModal && (
                <OmicsViewModal
                    isOpen={showOmicsModal}
                    onClose={() => setShowOmicsModal(false)}
                    omicsType={selectedOmicsType}
                    omicsId={selectedOmicsId}
                    omicsData={omicsModalData}
                />
            )}
        </div>
    );
}

export default ProjectDetails;